import { useRef } from 'react';
import { Form, FormField, FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthProviderV2, useRoutesProvider } from '@/providers/provider-hooks';
import { useProfileWithForm } from '@/domains/users/hooks/useProfileWithForm';
import { useFormNavigation, useConfirmationDialog } from '@/shared/hooks';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { CLASSES } from '@/constants/classes';
import {
  inFerestraSchimbareClasa,
  TEXT_FEREASTRA_SCHIMBARE_CLASA,
} from '@/domains/users/utils/fereastra-schimbare-clasa';
import { FaXmark, FaFileArrowUp, FaDownload } from 'react-icons/fa6';
import { buildProfilePayload } from '@/domains/users/validation/profile.schemas';
import { toastOneError } from '@/toast';
import { handleInvalidSubmit } from '@/shared/utils';

/**
 * ProfilePage - User profile editing with role-specific fields
 *
 * Refactored with Context7-validated patterns:
 * - Role-specific Zod validation (PROFESOR, ELEV, ADMIN)
 * - react-hook-form with dynamic schema
 * - shadcn Form components
 * - File upload for protocol documents (PROFESOR only)
 * - Change detection for save button state
 *
 * Reduced from 175 lines to ~180 lines (better structure, more features)
 */
export default function ProfilePage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const { userInfo } = useAuthProviderV2();
  const { ROLES } = useRoutesProvider();

  // O SINGURA sursa a rolului: `trigger()` si `buildProfilePayload` trebuie sa valideze cu
  // ACEEASI schema. Doua fallback-uri diferite ('ADMIN' vs '') ar fi putut alege scheme
  // diferite pentru validare si pentru construirea corpului.
  const rolFormular = userInfo?.role || 'ADMIN';

  const {
    form,
    onSubmit,
    loading,
    isDirty,
    handleFileUpload,
    removeFile,
    replaceAllProtocols,
    isUploadingFile,
  } = useProfileWithForm(rolFormular);

  // SE-28: evaluat o data pe randare — nu depinde de nimic din formular, doar de ziua de azi.
  const clasaFerestraDeschisa = inFerestraSchimbareClasa();

  // Navigation blocking when form has unsaved changes
  const navigationDialog = useConfirmationDialog({
    onConfirm: () => blocker.proceed?.(),
    onCancel: () => blocker.reset?.(),
  });

  const { blocker } = useFormNavigation({
    isDirty,
    onNavigationAttempt: navigationDialog.show,
  });

  // Submission confirmation
  const submitDialog = useConfirmationDialog({
    onConfirm: () => {
      // 🔴 NU `form.getValues()` brut (issue #14): `defaultValues` pune `telefon: ''` in stare
      // pentru ORICE rol, chiar si cand campul nu se randeaza. Schema rolului il elimina.
      const payload = buildProfilePayload(rolFormular, form.getValues());
      if (payload) {
        // Neasteptat cu intentie (dialogul se inchide imediat); `onSubmit` nu respinge niciodata.
        void onSubmit(payload as Parameters<typeof onSubmit>[0]);
      } else {
        // Nu inchide dialogul in tacere: un `safeParse` esuat aici ar fi exact familia de bug pe
        // care o reparam — utilizatorul apasa „Salvez", nu se intampla nimic, si nu afla de ce.
        toastOneError('Datele din formular nu au putut fi validate. Reincarca pagina si incearca din nou.');
      }
    },
  });

  const triggerFileInput = () => fileInputRef.current?.click();

  // Download first protocol document
  const handleDownloadFirst = () => {
    if (protocolDocs.length > 0) {
      const a = document.createElement('a');
      a.href = protocolDocs[0];
      a.download = 'protocol_de_colaborare.pdf';
      a.target = '_blank'; // Fallback if download fails
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Replace all protocol files
  const handleReplaceAll = () => {
    replaceAllProtocols();
    replaceInputRef.current?.click();
  };

  // Watch protocolDeColaborare for PROFESOR role
  const protocolDocs = form.watch('protocolDeColaborare' as any) || [];

  return (
    <div
      // `min-h-screen` centra cardul pe INALTIMEA FERESTREI, nu pe zona de continut:
      // sub bara de navigatie a aplicatiei, asta impinge formularul in jos si lasa o
      // banda goala sub el. `min-h-full` masoara containerul care il randeaza.
      //
      // `pt-12 md:pt-4` DOAR la PROFESOR: la 360 cardul lui e mai inalt decat zona, deci porneste
      // de sus, cu titlul (y=46) sub hamburgerul flotant (marginea de jos la 68). Cardul elevului
      // e mai scurt si ramane centrat (titlul la y=146), deci nu se atinge.
      className={`flex items-center justify-center min-h-full p-4${userInfo?.role === ROLES.PROFESOR ? ' pt-12 md:pt-4' : ''}`}
    >
      <Card className="w-full max-w-4xl">
        <CardHeader>
          <CardTitle>
            Formular de actualizare a datelor personale
            {isDirty && ' (Aveți modificări nesalvate în formular)'}
          </CardTitle>
        </CardHeader>

        <Form {...form}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const isValid = await form.trigger();
              if (isValid) {
                console.log('[DEBUG] Showing dialog...');
                submitDialog.show();
              } else {
                // Erorile de la Zod trebuie sa se poarte ca cele de la backend: scroll + focus pe
                // primul camp marcat, si un mesaj. Altfel utilizatorul apasa si nu se intampla nimic.
                handleInvalidSubmit();
              }
            }}
          >
            <CardContent className="space-y-4">
              {/* PROFESOR: codProfesor (readonly) */}
              {userInfo?.role === ROLES.PROFESOR && (
                <FormField
                  control={form.control}
                  name="codProfesor"
                  render={({ field }) => (
                    <div className="form-field-row">
                      <FormLabel className="form-label-bold">
                        Codul cu care elevii tăi își pot crea contul:
                      </FormLabel>
                      <FormItem className="flex-1 space-y-0">
                        <FormControl>
                          <Input
                            {...field}
                            className="form-input-styled"
                            disabled={true}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </FormItem>
                    </div>
                  )}
                />
              )}

              {/* ELEV: nume (nickname) + clasa */}
              {userInfo?.role === ROLES.ELEV && (
                <>
                  <FormField
                    control={form.control}
                    name="nume"
                    render={({ field }) => (
                      <div className="form-field-row">
                        <FormLabel className="form-label-bold">Nickname:</FormLabel>
                        <FormItem className="flex-1 space-y-0">
                          <FormControl>
                            <Input
                              {...field}
                              className="form-input-styled"
                              disabled={loading}
                              placeholder="Introduceți nickname-ul"
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </FormItem>
                      </div>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="clasa"
                    render={({ field }) => (
                      <div className="form-field-row">
                        <FormLabel className="form-label-bold">Clasa:</FormLabel>
                        <FormItem className="flex-1 space-y-0">
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={loading || !clasaFerestraDeschisa}
                          >
                            <FormControl>
                              <SelectTrigger className="form-select-trigger">
                                <SelectValue placeholder="Selectați clasa în care sunteți" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="form-select-content">
                              {CLASSES.map((cls) => (
                                <SelectItem key={cls.value} value={cls.value}>
                                  {cls.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {/* SE-28: backendul refuza deja cu 400 in afara ferestrei (owner,
                              gasit in PROD, 1e484a26) -- randul asta doar explica DINAINTE,
                              ca elevul sa nu incerce sa apese un select dezactivat fara sa
                              stie de ce. */}
                          {!clasaFerestraDeschisa && (
                            <p className="mt-1 text-small text-gray-600 m-0">
                              {TEXT_FEREASTRA_SCHIMBARE_CLASA}
                            </p>
                          )}
                          <FormMessage className="form-message-spacer" />
                        </FormItem>
                      </div>
                    )}
                  />
                </>
              )}

              {/* PROFESOR & ELEV: codSe (readonly) */}
              {userInfo?.role &&
                [ROLES.PROFESOR, ROLES.ELEV].includes(userInfo.role) && (
                  <FormField
                    control={form.control}
                    name="codSe"
                    render={({ field }) => (
                      <div className="form-field-row">
                        <FormLabel className="form-label-bold">Codul SE al școlii tale:</FormLabel>
                        <FormItem className="flex-1 space-y-0">
                          <FormControl>
                            <Input
                              {...field}
                              className="form-input-styled"
                              disabled={true}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </FormItem>
                      </div>
                    )}
                  />
                )}

              {/* PROFESOR & ADMIN: nume + telefon (side by side) */}
              {userInfo?.role !== ROLES.ELEV && (
                <div className="ecotic-form-row">
                  <FormField
                    control={form.control}
                    name="nume"
                    render={({ field }) => (
                      <div className="form-field-row">
                        <FormLabel className="form-label-bold">Nume:</FormLabel>
                        <FormItem className="flex-1 space-y-0">
                          <FormControl>
                            <Input
                              {...field}
                              className="form-input-styled"
                              disabled={loading}
                              placeholder="Introduceți numele"
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </FormItem>
                      </div>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="telefon"
                    render={({ field }) => (
                      <div className="form-field-row">
                        <FormLabel className="form-label-bold">Telefon:</FormLabel>
                        <FormItem className="flex-1 space-y-0">
                          <FormControl>
                            <Input
                              {...field}
                              type="tel"
                              className="form-input-styled"
                              disabled={loading}
                              placeholder="07xxxxxxxxx"
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage className="form-message-spacer" />
                        </FormItem>
                      </div>
                    )}
                  />
                </div>
              )}

              {/* ALL ROLES: email (readonly) */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <div className="form-field-row">
                    <FormLabel className="form-label-bold">Email:</FormLabel>
                    <FormItem className="flex-1 space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          className="form-input-styled"
                          disabled={true}
                        />
                      </FormControl>
                      <FormMessage className="form-message-spacer" />
                    </FormItem>
                  </div>
                )}
              />

              {/* PROFESOR: Protocol Documents Upload */}
              {userInfo?.role === ROLES.PROFESOR && (
                <div className="space-y-3">
                  <div className="flex flex-col gap-3">
                    {/* Sub `sm` eticheta si butonul stau unul sub altul: pe un rand, textul lung al
                        butonului (`whitespace-nowrap shrink-0` din Button) latea pagina la 440px pe
                        telefon. `sm:` e aceeasi treapta ca `sm:w-auto` de pe butoane. */}
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
                      <FormLabel className="form-label-bold">Protocol de colaborare:</FormLabel>
                      <Button
                        type="button"
                        variant="outline"
                        // Textul nu incape pe un rand in card la 360: se rupe, iar inaltimea creste
                        // peste 44px. De la `sm` in sus, exact clasele de dinainte.
                        className="w-full sm:w-auto h-auto min-h-11 whitespace-normal sm:h-11 sm:whitespace-nowrap md:h-9 md:min-h-0"
                        disabled={loading}
                        onClick={triggerFileInput}
                      >
                        <FaFileArrowUp className="mr-2" />
                        {isUploadingFile
                          ? 'Se încarcă...'
                          : 'Adaugă fișiere pentru protocolul de colaborare'}
                      </Button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(file);
                          }
                        }}
                      />
                    </div>

                    {/* Download and Replace buttons - show when files exist */}
                    {protocolDocs.length > 0 && (
                      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full sm:w-auto"
                          disabled={loading}
                          onClick={handleDownloadFirst}
                        >
                          <FaDownload className="mr-2" />
                          Descarcă Protocol Încărcat
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="w-full sm:w-auto"
                          disabled={loading}
                          onClick={handleReplaceAll}
                        >
                          <FaFileArrowUp className="mr-2" />
                          Înlocuiește Protocol
                        </Button>
                        <input
                          type="file"
                          ref={replaceInputRef}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleFileUpload(file);
                              // Reset file input to allow uploading same file again
                              if (replaceInputRef.current) {
                                replaceInputRef.current.value = '';
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Document List */}
                  {Array.isArray(protocolDocs) && protocolDocs.length > 0 && (
                    <div className="flex flex-col space-y-2 p-4 rounded-md border border-border bg-gray-50">
                      {protocolDocs.map((docUrl: string, index: number) => (
                        <div key={index} className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removeFile(index)}
                            disabled={loading}
                          >
                            <FaXmark />
                          </Button>
                          <Button
                            type="button"
                            variant="link"
                            className="flex h-auto flex-wrap px-0 text-left"
                            onClick={() => window.open(docUrl, '_blank')}
                          >
                            <span className="flex flex-wrap text-wrap break-all">
                              Document {index + 1}
                            </span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>

            <CardFooter className="justify-end mt-6">
              <Button
                type="submit"
                size="lg"
                disabled={!isDirty || loading}
                title={!isDirty ? 'Modificați formularul pentru a activa salvarea' : ''}
              >
                {loading ? 'Se salvează...' : 'Salvare modificări'}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>

      {/* Navigation confirmation - shown when trying to leave page with unsaved changes */}
      <ConfirmationDialog
        isOpen={navigationDialog.isOpen}
        onClose={navigationDialog.handleCancel}
        onConfirm={navigationDialog.handleConfirm}
        title="Modificări nesalvate"
        description="Aveți modificări nesalvate. Doriți să părăsiți pagina?"
        confirmText="Părăsește pagina"
        cancelText="Rămâi pe pagină"
        variant="destructive"
      />

      {/* Submission confirmation - shown when clicking save button */}
      <ConfirmationDialog
        isOpen={submitDialog.isOpen}
        onClose={submitDialog.handleCancel}
        onConfirm={submitDialog.handleConfirm}
        title="Confirmare salvare"
        description="Doriți să salvați modificările?"
        confirmText="Salvează"
        cancelText="Anulare"
        loading={loading}
      />
    </div>
  );
}
